import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import type { UpdateCustomLabelInput, UpdateCustomLabelMutation } from '@core/graphql/types';
import { track } from '@core/logging';
import { useState, useCallback } from 'react';

import { GET_CUSTOM_LABELS } from '../graphql/getCustomLabels';
import { UPDATE_CUSTOM_LABEL } from '../graphql/updateCustomLabel';

/**
 * Hook to update a custom label
 * @param communityId The ID of the community
 * @returns Mutation function, loading state, errors, and utilities
 */
export function useUpdateCustomLabel(communityId: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateCustomLabelMutation, { loading }] = useMutation<UpdateCustomLabelMutation>(UPDATE_CUSTOM_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updateCustomLabel = async (labelId: string, input: UpdateCustomLabelInput): Promise<boolean> => {
    try {
      setErrors(null);
      track('UpdateCustomLabelInitiated', {
        communityId,
        labelId,
      });

      await updateCustomLabelMutation({
        variables: { communityId, labelId, input },
        // Cache sync: refetch custom labels after update to ensure list consistency
        refetchQueries: [{ query: GET_CUSTOM_LABELS, variables: { communityId } }],
        awaitRefetchQueries: true,
      });

      track('UpdateCustomLabelSucceeded', {
        communityId,
        labelId,
      });

      return true;
    } catch (error) {
      track('UpdateCustomLabelFailed', {
        communityId,
        labelId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updateCustomLabel,
    loading,
    errors,
    resetErrors,
  };
}
